import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import ConsumptionHistory from "../../components/marketInventory/ConsumptionHistory";
import {
  fetchLocationsSuccess,
  fetchLocationsFailure,
  fetchConsumptionHistorySuccess,
  fetchConsumptionHistoryFailure,
  fetchConsumptionReportSuccess,
  fetchConsumptionReportFailure
} from "../../store/marketInventory/actions";
import * as API from "../../store/marketInventory/api";
import { isStaffUser, isSuperUser } from "../../utils/AuthService";

const ConsumptionHistoryContainer = ({
  consumption,
  locations,
  fetchLocationsSuccess,
  fetchLocationsFailure,
  fetchConsumptionHistorySuccess,
  fetchConsumptionHistoryFailure,
  fetchConsumptionReportSuccess,
  fetchConsumptionReportFailure
}) => {
  const isAdmin = isStaffUser() || isSuperUser();
  const [selectedLocationId, setSelectedLocationId] = useState(isAdmin ? null : "auto");

  // Fetch locations on mount (admin only - market users don't need to pick)
  useEffect(() => {
    if (!isAdmin) return;
    const loadLocations = async () => {
      try {
        const response = await API.fetchLocations();
        fetchLocationsSuccess(response.data);
        // Auto-select first location if available
        const locationsList = Array.isArray(response.data)
          ? response.data
          : (response.data.results || []);
        if (locationsList.length > 0) {
          setSelectedLocationId(locationsList[0].id);
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
        fetchLocationsFailure(error.response?.data?.message || "Failed to fetch locations");
      }
    };
    loadLocations();
  }, [isAdmin, fetchLocationsSuccess, fetchLocationsFailure]);

  const handleFetchConsumptionHistory = useCallback(async (locationId, fromDate, toDate) => {
    try {
      const locId = isAdmin ? locationId : undefined;
      const response = await API.fetchConsumptionHistory(locId, fromDate, toDate);
      fetchConsumptionHistorySuccess(response.data);
    } catch (error) {
      console.error("Error fetching consumption history:", error);
      const errorMessage = error.response?.data?.message || "Failed to fetch consumption history";
      fetchConsumptionHistoryFailure(errorMessage);
    }
  }, [isAdmin, fetchConsumptionHistorySuccess, fetchConsumptionHistoryFailure]);

  const handleFetchConsumptionReport = useCallback(async (locationId, groupBy, fromDate, toDate, itemId) => {
    try {
      const locId = isAdmin ? locationId : undefined;
      const response = await API.fetchConsumptionReport(locId, groupBy, fromDate, toDate, itemId);
      fetchConsumptionReportSuccess(response.data);
    } catch (error) {
      console.error("Error fetching consumption report:", error);
      const errorMessage = error.response?.data?.message || "Failed to fetch consumption report";
      fetchConsumptionReportFailure(errorMessage);
    }
  }, [isAdmin, fetchConsumptionReportSuccess, fetchConsumptionReportFailure]);

  const handleLocationChange = (e) => {
    const locationId = e.target.value ? parseInt(e.target.value) : null;
    setSelectedLocationId(locationId);
  };

  // Get selected location name
  const selectedLocationName = Array.isArray(locations.list)
    ? locations.list.find(l => l.id === selectedLocationId)?.name
    : null;

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>
            <i className="fa fa-history mr-05"></i>
            Consumption History
          </span>
          {isAdmin && (
            <div className="d-flex align-items-center">
              <label className="mb-0 mr-05" style={{ whiteSpace: 'nowrap' }}>
                <i className="fa fa-map-marker mr-05"></i>
                Location:
              </label>
              <select
                className="form-control"
                style={{ width: '200px' }}
                value={selectedLocationId || ""}
                onChange={handleLocationChange}
                disabled={locations.loading}
              >
                <option value="">Select location...</option>
                {Array.isArray(locations.list) && locations.list.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {selectedLocationId ? (
        <ConsumptionHistory
          consumption={consumption}
          loading={consumption.loading}
          error={consumption.error}
          fetchConsumptionHistory={handleFetchConsumptionHistory}
          fetchConsumptionReport={handleFetchConsumptionReport}
          locationId={selectedLocationId}
          locationName={selectedLocationName}
          isAdmin={isAdmin}
        />
      ) : (
        <div className="card">
          <div className="shipment-list--empty">
            <div className="fa fa-map-marker icon" aria-hidden="true"></div>
            <div>Please select a location to view consumption history</div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  consumption: state.marketInventory.consumption,
  locations: state.marketInventory.locations
});

const mapDispatchToProps = {
  fetchLocationsSuccess,
  fetchLocationsFailure,
  fetchConsumptionHistorySuccess,
  fetchConsumptionHistoryFailure,
  fetchConsumptionReportSuccess,
  fetchConsumptionReportFailure
};

export default connect(mapStateToProps, mapDispatchToProps)(ConsumptionHistoryContainer);
